import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toApiYmd } from '../../utils/dateUtil';
import { datePropsToNumber } from '../../utils/objectUtil';
import { facilityLotteryReservationCancelConfirmationUrl } from '../common/constant/appUrl';
import { selectLotteryReserveCancelEntry, setLotteryCancelReserve } from '../common/store/slices/lotteryReserveCancel';
import { lotteryMethod, yesNo } from '../common/constant/classification';
import { OperationId } from '../common/constant/operationLog';
import { DecodeMstCategory } from '../common/constant/decodeMst';
import { useErrorHandle } from '../common/error/errorHandler';
import { getFacility } from '../common/facility';
import { getUseReason } from '../common/useReason';
import { useOperationLog } from '../common/operationLog';
import { showLoading } from '../common/store/slices/application';
import { LotteryReservationCancel } from '../../views/components/common/lotteryReservationDetail/lotteryReservationDetailGItems';
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto';
import {
  FacilityInfoType,
  LOTTERY_RESERVATION_DECODE_MST,
  getLotteryReservation,
  setStringifyNamesChildInfoList,
  stringifySelectBox
} from '../common/lotteryReservation';
import { getProjectMstDataToRefer } from '../common/projectMst';
import { ValueLabels } from '../../views/components/common/inputs/select';
import { getDecodeMstValues, GetDecodemstValuesReturn } from '../common/decodeMst';
import { selectSystemControl } from '../common/store/slices/systemControl';
import { MEDICAL_DOC_USAGE } from '../common/constant/projectMst';
import { isUseReasonCategoryHidden } from '../common/reservation';

interface UrlParams {
  lotteryReservationNo: string
};

interface LocationState {
  isKeep: boolean // 取得・入力済み情報から復元を試みる場合true
  decodeMst: FacilityLotteryCancelDecodeMst
};

interface Inputs {
  cancelReason?: string
};

interface ReservationDetail extends LotteryReservationCancel {
  useReason: GetUseReasonDto
  updateDatetime: string
};

// デコードマスタ
const LOTTERY_CANCEL_DECODE_MST = {
  lotteryCancelReason: DecodeMstCategory.LOTTERY_CANCEL_REASON,
  ... LOTTERY_RESERVATION_DECODE_MST
};
type FacilityLotteryCancelDecodeMst = GetDecodemstValuesReturn<typeof LOTTERY_CANCEL_DECODE_MST>;

export const useAction = () => {
  const errorHandle = useErrorHandle();
  const dispatch = useDispatch();
  const history = useHistory<LocationState | undefined>();
  const { lotteryReservationNo } = useParams<UrlParams>();
  const { addOperationLog } = useOperationLog();
  const lotteryReservation = useSelector(selectLotteryReserveCancelEntry);
  const locationState = history.location.state;
  const sysCtrl = useSelector(selectSystemControl)
  const showSiblingsLotteryResultReferenceFlag = sysCtrl.lotteryMethod === lotteryMethod.byParent

  const [decodeMst, setDecodeMst] = useState<FacilityLotteryCancelDecodeMst>();
  const [reservationDetail, setReservationDetail] = useState<ReservationDetail | undefined>();
  const facility = useRef<FacilityInfoType>();
  const cancelReasonOptions = useMemo<ValueLabels[] | null>(() => {
    if (!decodeMst) return null;
    return decodeMst.lotteryCancelReason.map((reason)=> ({
      value: reason.code,
      label: reason.name
    }))
  }, [decodeMst]);

  // フォーム関連
  const formMethods = useForm<Inputs>();

  // 初期化
  useEffect(() => {
    dispatch(
      showLoading(
        errorHandle(async() => {
          let cancelReason: string | undefined;
          let reservationDetail: ReservationDetail;
          let decodeMst: FacilityLotteryCancelDecodeMst;
          if (locationState && locationState.isKeep && lotteryReservation) {
            // 次ページから戻ってきた場合
            const { usageDatetimes, facility: facilityData, goingTimes, ... other } = lotteryReservation;

            cancelReason = other.cancelReason.select;
            reservationDetail = {
              ... other,
              ... facilityData,
              status: usageDatetimes[0].status,
              goingTime: goingTimes[0],
              usageDate: usageDatetimes[0].usageDate,
              useFromDatetime: usageDatetimes[0].useFromDatetime,
              useToDatetime: usageDatetimes[0].useToDatetime,
            };
            decodeMst = locationState.decodeMst;
            facility.current = facilityData;
          } else {
            // 初期表示の場合
            const [
              lotteryReservation, 
              useReason,
              decodeMstData
            ] = await Promise.all([
              getLotteryReservation(lotteryReservationNo),
              getUseReason(),
              getDecodeMstValues(LOTTERY_CANCEL_DECODE_MST)
            ]);

            const facilityData = await getFacility(lotteryReservation.facilityId, lotteryReservation.usageDate)
            const projectMst = await getProjectMstDataToRefer(lotteryReservation.facilityId, [lotteryReservation.usageDate], facilityData.projectId)
            projectMst.medicalDocUsage = projectMst.medicalDocUsage === MEDICAL_DOC_USAGE.BY_FACILITY && facilityData.detailSetting[0].medicalDoc !== null ? facilityData.detailSetting[0].medicalDoc : projectMst.medicalDocUsage

            reservationDetail = {
              ... lotteryReservation,
              ... facilityData,
              projectMst,
              useReason,
              childInfoList: setStringifyNamesChildInfoList(lotteryReservation.childInfoList, decodeMstData),
              formUseReason: {
                ...lotteryReservation.formUseReason,
                stringify: (decodeMstData) 
                    ? stringifySelectBox(
                      decodeMstData.formUseReason, 
                      lotteryReservation.formUseReason?.select, 
                      lotteryReservation.formUseReason?.other,
                      ["その他"]
                    ): undefined
              },
              isUseReasonCategoryHidden: isUseReasonCategoryHidden(lotteryReservation.useReasonCategory, facilityData),
            }
            decodeMst = decodeMstData;
            facility.current = {
              ... facilityData,
              facilityId: lotteryReservation.facilityId
            }
          }

          // アクセスログ
          addOperationLog({
            operationId: OperationId.OP_00000001,
            accessData: [
              ...reservationDetail.childInfoList.map((child) => ({
                userIdRegFlag: yesNo.yes,
                childId: child.childId,
                usageDate: toApiYmd(reservationDetail.usageDate),
                lotteryReservationNo
              }))
            ],
          })

          formMethods.reset({
            cancelReason
          })
          setReservationDetail(reservationDetail);
          setDecodeMst(decodeMst);
        })
      )
    )
  }, []);

  const goList = useCallback(() => history.goBack(), []);

  const onSubmit = useCallback((data: Inputs) => {
    addOperationLog({ operationId: OperationId.OP_000001734 });
    if (!reservationDetail || !facility.current || !decodeMst) return;
    const cancelReason = data.cancelReason;
    if (!cancelReason) return;

    const { usageDate, useFromDatetime, useToDatetime, status, goingTime, ... other } = reservationDetail;

    dispatch(
      setLotteryCancelReserve({
        ...datePropsToNumber(other),
        usageDatetimes: [{
          ...datePropsToNumber({
            usageDate,
            useFromDatetime,
            useToDatetime,
            status
          }),
          usageBaseDate: toApiYmd(usageDate)
        }],
        facility: facility.current,
        goingTimes: [goingTime],
        cancelReason: {
          select: cancelReason,
          stringify: decodeMst.lotteryCancelReason.find((reason) => reason.code === cancelReason)?.name
        },
        reservationNo: lotteryReservationNo
      })
    )
    // 戻るで表示した際に取得・入力済み情報から復元を試みる為に履歴に保管
    history.replace({ ...history.location, state: { 
      isKeep: true,
      decodeMst: decodeMst
    }})
    history.push(facilityLotteryReservationCancelConfirmationUrl.url())
  }, [
    reservationDetail,
    facility,
    decodeMst,
    addOperationLog
  ]);

  return {
    decodeMst,
    reservationDetail,
    cancelReasonOptions,
    formMethods,
    showSiblingsLotteryResultReferenceFlag,
    goList,
    onSubmit
  }
}
